import { LocationType, PropertyType } from '~/client/graph/types.generated'
import type { HierarchyTreeOption } from '~/client/src/shared/utils/hierarchyTree'

import {
  isProjectLocationKind,
  kindFromIconName,
  kindFromLocationType,
  LOCATION_KIND_PROPERTY,
  LOCATION_KIND_SLUGS,
  type LocationKindValue,
  rootOfKind,
} from './locationKind'
import type {
  ResourcePropertiesState,
  ResourcePropertyDto,
} from './resourceSchema'

type KindOption = Omit<HierarchyTreeOption, 'label'>

/**
 * The shipped Type list, `LocationTypeHierarchy.BuildOptions()`: what a new project is seeded
 * with, and what the server validates a project with no stored list against.
 */
export const SHIPPED_LOCATION_KIND_OPTIONS: readonly KindOption[] =
  LOCATION_KIND_SLUGS.map(value => {
    const root = rootOfKind(value)
    return { value, parentValue: root === value ? undefined : root }
  })

/** The options of a project's stored Type list; undefined when it has none. */
export const locationTypeOptionsOf = (
  properties: readonly ResourcePropertyDto[],
): ReadonlyArray<HierarchyTreeOption> | undefined =>
  properties.find(
    property =>
      property.name === LOCATION_KIND_PROPERTY &&
      property.propertyType === PropertyType.Hierarchy,
  )?.configuration?.options

/**
 * Whether the server creates a location of this kind in a project with these stored Type options
 * (`LocationSaveHandler.ValidateKindOnCreateAsync`). An empty list is read as the shipped one. The
 * kind must be in the list and in the shipped table, not deleted itself (a deleted parent does
 * not count), and no live option may sit under it. The site root never is: a project has one.
 */
export function isLocationKindCreatable(
  kind: LocationKindValue | null | undefined,
  stored?: ReadonlyArray<KindOption> | null,
): boolean {
  if (!kind) return false

  const options = stored?.length ? stored : SHIPPED_LOCATION_KIND_OPTIONS
  const own = options.find(option => option.value === kind)

  return (
    !!own &&
    LOCATION_KIND_SLUGS.includes(kind) &&
    !own.deleted &&
    !options.some(option => !option.deleted && option.parentValue === kind) &&
    !isProjectLocationKind(kind)
  )
}

/** {@link isLocationKindCreatable} for one list, decided once for every kind. */
export function creatableLocationKinds(
  stored?: ReadonlyArray<KindOption> | null,
): (kind: LocationKindValue | null | undefined) => boolean {
  const creatable = new Set(
    LOCATION_KIND_SLUGS.filter(kind => isLocationKindCreatable(kind, stored)),
  )
  return kind => !!kind && creatable.has(kind)
}

export interface ICreatableLocationKinds {
  /** Whether the project's Type list is known. Until it is, no kind is creatable. */
  readonly isReady: boolean
  /** Whether loading the Type list failed with none known. */
  readonly isFailed: boolean
  readonly isCreatable: (kind: LocationKindValue | null | undefined) => boolean
}

const NOT_READY: ICreatableLocationKinds = {
  isReady: false,
  isFailed: false,
  isCreatable: () => false,
}

const FAILED: ICreatableLocationKinds = { ...NOT_READY, isFailed: true }

/**
 * The kinds a project can create, from its location properties; none without them. A list kept
 * through a refresh, or after a reload failed, still counts: it is this project's last loaded one
 * (the store drops another project's), and the server refuses whatever changed since.
 */
export const creatableLocationKindsOf = (
  schema: ResourcePropertiesState | null | undefined,
): ICreatableLocationKinds => {
  if (!schema?.propertiesKnown) {
    return schema?.propertiesError ? FAILED : NOT_READY
  }
  return {
    isReady: true,
    isFailed: false,
    isCreatable: creatableLocationKinds(
      locationTypeOptionsOf(schema.properties),
    ),
  }
}

/**
 * The kind a location created from a palette entry, a menu item or an armed tool gets: the kind it
 * carries, else the one its icon names under its type's root. Null for one that creates no
 * location (equipment, a sticker). The creation card seeds the same, so every surface judges it.
 */
export const createdLocationKind = (
  kind: LocationKindValue | null | undefined,
  iconName: string | null | undefined,
  type: LocationType | null | undefined,
): LocationKindValue | null =>
  kind ?? kindFromIconName(iconName, kindFromLocationType(type))
